import { Component, EventEmitter, Inject, Input, OnChanges, Optional, Output, SimpleChanges } from '@angular/core';
import { GeographicalSummaryConfig } from '../../../config/config.model';
import { GEOGRAPHICAL_SUMMARY_CONFIG_TOKEN } from '../../../config/config.tokens';
import { GeographicalChartPoint } from '../../data-access/geographical-summary.types';

interface ChartSegment {
    point: GeographicalChartPoint;
    x: number;
    y: number;
    width: number;
    height: number;
    stageClass: string;
}

interface YearTick {
    label: string;
    x: number;
}

@Component({
    selector: 'app-geographical-summary-chart',
    templateUrl: './geographical-summary-chart.component.html',
    styleUrls: ['./geographical-summary-chart.component.scss']
})
export class GeographicalSummaryChartComponent implements OnChanges {
    @Input() points: GeographicalChartPoint[] = [];
    @Output() pointSelected = new EventEmitter<GeographicalChartPoint>();

    readonly viewWidth = 860;
    readonly viewHeight = 280;
    readonly chartLeft = 36;
    readonly chartRight = 12;
    readonly chartTop = 8;
    readonly chartBottom = 28;

    segments: ChartSegment[] = [];
    ticks: YearTick[] = [];
    selectedKey: string | null = null;

    private readonly defaultYearHistory = 7;

    constructor(
        @Optional() @Inject(GEOGRAPHICAL_SUMMARY_CONFIG_TOKEN) private readonly geographicalSummaryConfig: GeographicalSummaryConfig | null
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.points) {
            this.rebuildChart();
        }
    }

    selectPoint(point: GeographicalChartPoint): void {
        this.selectedKey = this.pointKey(point);
        this.pointSelected.emit(point);
    }

    isSelected(point: GeographicalChartPoint): boolean {
        return this.selectedKey === this.pointKey(point);
    }

    private rebuildChart(): void {
        const chartWidth = this.viewWidth - this.chartLeft - this.chartRight;
        const chartHeight = this.viewHeight - this.chartTop - this.chartBottom;
        if (chartWidth <= 0 || chartHeight <= 0) {
            this.segments = [];
            this.ticks = [];
            return;
        }

        const currentYear = new Date().getFullYear();
        const yearHistory = this.getYearHistory();
        const years = Array.from({ length: yearHistory }, (_, index) => currentYear - yearHistory + 1 + index);

        const byYear = new Map<number, GeographicalChartPoint[]>();
        for (const point of this.points) {
            const year = Number(point.startupYear);
            if (!Number.isFinite(year)) {
                continue;
            }

            if (!byYear.has(year)) {
                byYear.set(year, []);
            }
            byYear.get(year)!.push(point);
        }

        const pointsInWindow = years.flatMap((year) => byYear.get(year) || []);
        const maxTotal = Math.max(
            ...years.map((year) => (byYear.get(year) || []).reduce((sum, item) => sum + Math.max(0, Number(item.value) || 0), 0)),
            1
        );

        const barGap = 8;
        const barWidth = Math.max(16, (chartWidth - Math.max(0, years.length - 1) * barGap) / Math.max(1, years.length));

        const stageKeys = Array.from(new Set(pointsInWindow.map((point) => point.stageKey || point.stageLabel || 'Unknown')));
        const stageClassLookup = new Map<string, string>(stageKeys.map((key, index) => [key, `stage-${index % 5}`]));

        const nextSegments: ChartSegment[] = [];
        const nextTicks: YearTick[] = [];

        years.forEach((year, yearIndex) => {
            const x = this.chartLeft + yearIndex * (barWidth + barGap);
            const yearPoints = (byYear.get(year) || []).slice().sort((a, b) => {
                const aKey = a.stageKey || a.stageLabel || '';
                const bKey = b.stageKey || b.stageLabel || '';
                return aKey.localeCompare(bKey);
            });

            let stackedHeight = 0;
            for (const point of yearPoints) {
                const value = Math.max(0, Number(point.value) || 0);
                const height = (value / maxTotal) * chartHeight;
                const y = this.chartTop + chartHeight - stackedHeight - height;

                nextSegments.push({
                    point,
                    x,
                    y,
                    width: barWidth,
                    height,
                    stageClass: stageClassLookup.get(point.stageKey || point.stageLabel || 'Unknown') || 'stage-0'
                });

                stackedHeight += height;
            }

            nextTicks.push({
                label: `${year}`,
                x: x + barWidth / 2
            });
        });

        this.segments = nextSegments.filter((segment) => segment.height > 0);
        this.ticks = nextTicks;
    }

    private pointKey(point: GeographicalChartPoint): string {
        return `${point.startupYear}|${point.stageKey}|${point.value}`;
    }

    private getYearHistory(): number {
        const configuredYearHistory = Number(this.geographicalSummaryConfig?.chart?.yearHistory);
        if (!Number.isFinite(configuredYearHistory)) {
            return this.defaultYearHistory;
        }

        return Math.max(1, Math.floor(configuredYearHistory));
    }
}
